import { apiService } from './api';

export interface Feed {
  _id: string;
  name: string;
  url: string;
  description?: string;
  isActive: boolean;
  lastFetchedAt?: string;
  articleCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFeedRequest {
  name: string;
  url: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateFeedRequest {
  name?: string;
  url?: string;
  description?: string;
  isActive?: boolean;
}

export interface BulkCreateFeedsRequest {
  urls: string[];
}

export interface FeedsResponse {
  success: boolean;
  data: Feed[];
  message?: string;
}

export interface FeedResponse {
  success: boolean;
  data: Feed;
  message?: string;
}

class FeedsService {
  private baseUrl = '/feeds';

  /**
   * Get all feeds with optional filtering
   */
  async getFeeds(active?: boolean, search?: string): Promise<Feed[]> {
    try {
      return await apiService.getFeeds(active, search);
    } catch (error: any) {
      console.error('Failed to fetch feeds:', error);
      throw new Error(
        `Failed to fetch feeds: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Get a single feed by ID
   */
  async getFeed(id: string): Promise<Feed> {
    try {
      return await apiService.getFeed(id);
    } catch (error: any) {
      console.error(`Failed to fetch feed ${id}:`, error);
      throw new Error(
        `Failed to fetch feed: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Create a new feed
   */
  async createFeed(feedData: CreateFeedRequest): Promise<Feed> {
    try {
      return await apiService.createFeed(feedData);
    } catch (error: any) {
      console.error('Failed to create feed:', error);
      throw new Error(
        `Failed to create feed: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Update an existing feed
   */
  async updateFeed(id: string, feedData: UpdateFeedRequest): Promise<Feed> {
    try {
      return await apiService.updateFeed(id, feedData);
    } catch (error: any) {
      console.error(`Failed to update feed ${id}:`, error);
      throw new Error(
        `Failed to update feed: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Delete a feed
   */
  async deleteFeed(id: string): Promise<void> {
    try {
      await apiService.deleteFeed(id);
    } catch (error: any) {
      console.error(`Failed to delete feed ${id}:`, error);
      throw new Error(
        `Failed to delete feed: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Fetch and update feed data
   */
  async fetchFeed(id: string): Promise<Feed> {
    try {
      return await apiService.fetchFeed(id);
    } catch (error: any) {
      console.error(`Failed to fetch feed data for ${id}:`, error);
      throw new Error(
        `Failed to fetch feed data: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Get articles for a specific feed
   */
  async getFeedArticles(id: string, options: any = {}): Promise<any> {
    try {
      return await apiService.getFeedArticles(id, options);
    } catch (error: any) {
      console.error(`Failed to get articles for feed ${id}:`, error);
      throw new Error(
        `Failed to get articles: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Get all articles across all feeds
   */
  async getAllArticles(options: any = {}): Promise<any> {
    try {
      return await apiService.getAllArticles(options);
    } catch (error: any) {
      console.error('Failed to get all articles:', error);
      throw new Error(
        `Failed to get articles: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Get article statistics
   */
  async getArticleStats(): Promise<any> {
    try {
      return await apiService.getArticleStats();
    } catch (error: any) {
      console.error('Failed to get article stats:', error);
      throw new Error(
        `Failed to get article stats: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Mark article as read/unread
   */
  async markArticleAsRead(id: string, isRead: boolean): Promise<any> {
    try {
      return await apiService.markArticleAsRead(id, isRead);
    } catch (error: any) {
      console.error(`Failed to mark article ${id} as read:`, error);
      throw new Error(
        `Failed to mark article as read: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Toggle article bookmark status
   */
  async toggleArticleBookmark(id: string): Promise<any> {
    try {
      return await apiService.toggleArticleBookmark(id);
    } catch (error: any) {
      console.error(`Failed to toggle bookmark for article ${id}:`, error);
      throw new Error(
        `Failed to toggle bookmark: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Fetch all active feeds
   */
  async fetchAllActiveFeeds(): Promise<Feed[]> {
    try {
      const feeds = await this.getFeeds(true);
      const results: Feed[] = [];
      const errors: string[] = [];

      for (const feed of feeds) {
        try {
          const updatedFeed = await this.fetchFeed(feed._id);
          results.push(updatedFeed);
        } catch (error: any) {
          errors.push(`Failed to fetch ${feed.name}: ${error.message}`);
        }
      }

      if (errors.length > 0) {
        console.warn('Some feeds failed to fetch:', errors);
      }

      return results;
    } catch (error: any) {
      console.error('Failed to fetch all active feeds:', error);
      throw new Error(
        `Failed to fetch all active feeds: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Create multiple feeds from URLs
   */
  async createBulkFeeds(urls: string[]): Promise<Feed[]> {
    try {
      return await apiService.createBulkFeeds(urls);
    } catch (error: any) {
      console.error('Failed to create bulk feeds:', error);
      throw new Error(
        `Failed to create bulk feeds: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Toggle feed active status
   */
  async toggleFeedStatus(id: string, isActive: boolean): Promise<Feed> {
    return this.updateFeed(id, { isActive });
  }

  /**
   * Get feed statistics
   */
  async getFeedStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
    recentlyFetched: number;
    neverFetched: number;
  }> {
    try {
      const feeds = await this.getFeeds();
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

      const stats = {
        total: feeds.length,
        active: feeds.filter((f) => f.isActive).length,
        inactive: feeds.filter((f) => !f.isActive).length,
        recentlyFetched: feeds.filter(
          (f) => f.lastFetchedAt && new Date(f.lastFetchedAt) > oneDayAgo,
        ).length,
        neverFetched: feeds.filter((f) => !f.lastFetchedAt).length,
      };

      return stats;
    } catch (error: any) {
      console.error('Failed to get feed stats:', error);
      throw new Error(
        `Failed to get feed stats: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Validate RSS feed URL
   */
  async validateFeedUrl(
    url: string,
  ): Promise<{ isValid: boolean; title?: string; description?: string }> {
    try {
      return await apiService.validateFeedUrl(url);
    } catch (error: any) {
      return { isValid: false };
    }
  }

  /**
   * Get popular RSS feed URLs for different categories
   */
  getPopularFeedUrls(): { [category: string]: string[] } {
    return {
      'Financial News': [
        'https://feeds.reuters.com/reuters/businessNews',
        'https://feeds.bloomberg.com/markets/news.rss',
        'https://www.cnbc.com/id/100003114/device/rss/rss.html',
        'https://feeds.marketwatch.com/marketwatch/topstories/',
        'https://www.ft.com/rss/home',
      ],
      Technology: [
        'https://feeds.feedburner.com/TechCrunch/',
        'https://www.wired.com/feed/rss',
        'https://feeds.arstechnica.com/arstechnica/index',
        'https://www.theverge.com/rss/index.xml',
        'https://feeds.feedburner.com/venturebeat/SZYF',
      ],
      Crypto: [
        'https://cointelegraph.com/rss',
        'https://cryptonews.com/news/feed',
        'https://bitcoinmagazine.com/.rss/full/',
        'https://decrypt.co/feed',
        'https://www.coindesk.com/arc/outboundfeeds/rss/',
      ],
      'General News': [
        'https://feeds.reuters.com/Reuters/worldNews',
        'https://rss.cnn.com/rss/edition.rss',
        'https://feeds.bbci.co.uk/news/rss.xml',
        'https://www.npr.org/rss/rss.php?id=1001',
        'https://feeds.nbcnews.com/nbcnews/public/world',
      ],
    };
  }

  /**
   * Format feed data for display
   */
  formatFeedData(feed: Feed): any {
    const now = new Date();
    const lastFetched = feed.lastFetchedAt
      ? new Date(feed.lastFetchedAt)
      : null;
    const timeDiff = lastFetched ? now.getTime() - lastFetched.getTime() : null;
    const hoursDiff = timeDiff ? Math.floor(timeDiff / (1000 * 60 * 60)) : null;

    // Determine status
    let statusText = 'Never fetched';
    let statusColor = 'red';

    if (lastFetched && hoursDiff !== null) {
      if (hoursDiff < 1) {
        statusText = 'Just fetched';
        statusColor = 'green';
      } else if (hoursDiff < 24) {
        statusText = `${hoursDiff}h ago`;
        statusColor = 'green';
      } else if (hoursDiff < 168) {
        statusText = `${Math.floor(hoursDiff / 24)}d ago`;
        statusColor = 'yellow';
      } else {
        statusText = `${Math.floor(hoursDiff / 24)}d ago`;
        statusColor = 'red';
      }
    }

    return {
      lastFetchedFormatted: lastFetched
        ? lastFetched.toLocaleString()
        : 'Never',
      statusColor,
      statusText,
    };
  }
}

export const feedsService = new FeedsService();
